import { Box, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { User01Icon } from '@/components/icons/User01Icon';
import { XCloseIcon } from '@/components/icons/XCloseIcon';
import { ActionCard } from '@/components/layout/ActionCard/ActionCard';
import { VerticalTabs } from '@/components/navigation/VerticalTabs/VerticalTabs';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { COLORS } from '@/styles/tokens/colors';
import { getNodes } from '@/utils/graphqlUtils';

import { BusinessReviewSuggestions } from './ReviewSuggestion.Businesses';
import { DirectHoldingsReviewSuggestions } from './ReviewSuggestion.DirectHoldings';
import { ReviewSuggestionLoadingState } from './ReviewSuggestion.LoadingState';
import { TrustReviewSuggestions } from './ReviewSuggestion.Trusts';
import { useReviewSuggestionCountsQuery } from './ReviewSuggestions.generated';
import {
  getInitiallyVisibleCategories,
  getReviewSuggestionCounts,
  REVIEW_SUGGESTION_CATEGORY_LABELS,
  REVIEW_SUGGESTION_CATEGORY_ORDER,
  ReviewSuggestionCategory,
} from './ReviewSuggestions.utils';

interface ReviewSuggestionsProps {
  onClose: () => void;
}

function CategoryEmptyState() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      p={4}
      height="100%"
      minHeight={400}
      border={`1px solid ${COLORS.GRAY[200]}`}
      borderRadius="4px"
    >
      <ActionCard
        variant="noCard"
        icon={<User01Icon size={32} color={COLORS.TEXT.PRIMARY} />}
        heading="No remaining suggestions"
        description="All suggestions have been created"
        sx={{ maxWidth: 400 }}
      />
    </Stack>
  );
}

export function ReviewSuggestions({ onClose }: ReviewSuggestionsProps) {
  const { defaultFirstToDieClientId, householdId } =
    useHouseholdDetailsContext();
  const { data, loading } = useReviewSuggestionCountsQuery({
    variables: { householdId: householdId! },
    skip: !householdId,
    fetchPolicy: 'cache-and-network',
  });
  const queriedCounts = useMemo(
    () =>
      getReviewSuggestionCounts(
        getNodes(data?.aiSuggestions),
        defaultFirstToDieClientId ?? undefined
      ),
    [data?.aiSuggestions, defaultFirstToDieClientId]
  );
  const [visibleCategories, setVisibleCategories] = useState<
    ReviewSuggestionCategory[] | null
  >(null);
  const [activeCategory, setActiveCategory] =
    useState<ReviewSuggestionCategory | null>(null);

  useEffect(() => {
    if (loading) return;

    // Add categories as roots publish suggestions. Keep visited categories and
    // the selected tab when their counts reach zero during review.
    const readyCategories = getInitiallyVisibleCategories(queriedCounts);
    const nextCategories = REVIEW_SUGGESTION_CATEGORY_ORDER.filter(
      (category) =>
        visibleCategories?.includes(category) ||
        readyCategories.includes(category)
    );
    if (
      visibleCategories === null ||
      nextCategories.length !== visibleCategories.length
    ) {
      setVisibleCategories(nextCategories);
      setActiveCategory((current) => current ?? nextCategories[0] ?? null);
    }
  }, [loading, queriedCounts, visibleCategories]);

  const categoryContent = useMemo(() => {
    if (visibleCategories === null) {
      return <ReviewSuggestionLoadingState />;
    }

    if (!activeCategory || queriedCounts[activeCategory] === 0) {
      return <CategoryEmptyState />;
    }

    switch (activeCategory) {
      case ReviewSuggestionCategory.DirectHoldings:
        return <DirectHoldingsReviewSuggestions />;
      case ReviewSuggestionCategory.Trusts:
        return <TrustReviewSuggestions />;
      case ReviewSuggestionCategory.Businesses:
        return <BusinessReviewSuggestions />;
    }
  }, [activeCategory, queriedCounts, visibleCategories]);

  const tabs = (visibleCategories ?? []).map((category) => ({
    value: category,
    display: REVIEW_SUGGESTION_CATEGORY_LABELS[category],
    badgeCount: queriedCounts[category],
  }));

  return (
    <Stack height="100vh" bgcolor="common.white">
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        height="60px"
        minHeight="60px"
        px={2}
        borderBottom={`1px solid ${COLORS.GRAY[200]}`}
      >
        <Typography variant="title-l">Review suggestions</Typography>
        <IconButton
          ariaLabel="Close review suggestions"
          icon={XCloseIcon}
          variant="transparent"
          size="sm"
          onClick={onClose}
        />
      </Stack>
      <Stack direction="row" flex={1} minHeight={0} p={2} spacing={3}>
        {tabs.length > 0 && activeCategory && (
          <Box width="240px" flexShrink={0}>
            <VerticalTabs
              tabs={tabs}
              activeValue={activeCategory}
              onChange={setActiveCategory}
              ariaLabel="Suggestion categories"
            />
          </Box>
        )}
        <Box flex={1} minWidth={0} overflow="auto">
          {categoryContent}
        </Box>
      </Stack>
    </Stack>
  );
}
